import { z } from "zod"

import {
  branchIdSchema,
  correlationIdSchema,
  entityIdSchema,
  legalEntityIdSchema,
} from "./identifiers.js"

export const fleetAssetKindV1Schema = z.enum(["vehicle", "equipment"])
export const fleetMeterKindV1Schema = z.enum(["odometer", "hours"])
export const fleetAvailabilityV1Schema = z.enum(["available", "unavailable"])
export const fleetOperatingStatusV1Schema = z.enum([
  "Available",
  "Maintenance Due",
  "Out of Service",
])

export const fleetAssetAdministerV1Schema = z
  .object({
    requestVersion: z.literal(1),
    correlationId: correlationIdSchema,
    idempotencyKey: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/),
    assetId: entityIdSchema,
    revisionId: entityIdSchema,
    statusEventId: entityIdSchema,
    legalEntityId: legalEntityIdSchema,
    homeBranchId: branchIdSchema,
    expectedVersion: z.number().int().nonnegative(),
    assetKind: fleetAssetKindV1Schema,
    assetCode: z.string().trim().min(2).max(64).regex(/^[A-Za-z0-9][A-Za-z0-9._-]*$/),
    displayName: z.string().trim().min(2).max(140),
    availability: fleetAvailabilityV1Schema,
    meterKind: fleetMeterKindV1Schema,
    meterMilli: z.number().int().nonnegative(),
    registrationNumber: z.string().trim().min(2).max(64).nullable(),
    registrationExpiresOn: z.string().date().nullable(),
    policyReference: z.string().trim().min(2).max(128).nullable(),
    policyExpiresOn: z.string().date().nullable(),
    operatingStatus: fleetOperatingStatusV1Schema,
    statusCause: z.string().trim().min(3).max(256),
    effectiveAt: z.string().datetime({ offset: true }),
  })
  .strict()
  .superRefine((value, context) => {
    if (
      (value.assetKind === "vehicle") !== (value.registrationNumber !== null) ||
      (value.registrationNumber === null) !==
        (value.registrationExpiresOn === null)
    ) {
      context.addIssue({
        code: "custom",
        message: "Vehicle registration fields must be supplied together.",
      })
    }
  })

export const fleetAssetResultV1Schema = z
  .object({
    entityType: z.literal("fleet_asset"),
    entityId: entityIdSchema,
    state: fleetOperatingStatusV1Schema,
    version: z.number().int().positive(),
    legalEntityId: legalEntityIdSchema,
    homeBranchId: branchIdSchema,
    assetKind: fleetAssetKindV1Schema,
    assetCode: z.string(),
    availability: fleetAvailabilityV1Schema,
    meterKind: fleetMeterKindV1Schema,
    meterMilli: z.number().int().nonnegative(),
    registrationNumber: z.string().nullable(),
    registrationExpiresOn: z.string().date().nullable(),
    policyReference: z.string().nullable(),
    policyExpiresOn: z.string().date().nullable(),
    operatingStatus: fleetOperatingStatusV1Schema,
    statusCause: z.string(),
    effectiveAt: z.string(),
    assignable: z.boolean(),
  })
  .strict()

export type FleetAssetAdministerV1 = z.infer<typeof fleetAssetAdministerV1Schema>
export type FleetAssetResultV1 = z.infer<typeof fleetAssetResultV1Schema>
